import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import { trackClick } from "../lib/analytics";
import { Newspaper, ArrowRight, ChevronLeft, ChevronRight, Clock, MessageSquare, Heart } from "lucide-react";

const PAGE_SIZE = 24;

function timeAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso);
  if (isNaN(t.getTime())) return null;
  const diff = (Date.now() - t.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NewsIndex() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/news/articles", { params: { page, page_size: PAGE_SIZE } })
      .then((r) => setData(r.data))
      .catch(() => setData({ articles: [], total: 0 }))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const totalPages = data ? Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)) : 1;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 py-8 sm:py-12">
      <Helmet>
        <title>Internet Outage News · SpeedTestTools</title>
        <meta name="description" content="Live coverage of internet outages, ISP problems and slow connections. Fresh headlines updated every hour." />
        <link rel="canonical" href="https://speedtesttools.com/news" />
        <meta property="og:title" content="Internet Outage News · SpeedTestTools" />
        <meta property="og:description" content="Live coverage of internet outages, ISP problems and slow connections." />
        <meta property="og:type" content="website" />
      </Helmet>

      <header className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-2">/ News</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Internet outage news</h1>
        <p className="mt-3 text-sm sm:text-base text-[var(--text-dim)] max-w-2xl">
          Every hour we pull fresh headlines about internet outages, ISP issues and slow connections from across the web — with a link back to the source and a place to compare notes with other readers.
        </p>
      </header>

      {loading && !data ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="glass rounded-xl h-40 animate-pulse" />)}
        </div>
      ) : !data || (data.articles || []).length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-[var(--text-dim)]">No stories yet — check back in an hour.</div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="news-index-grid">
            {data.articles.map((a) => (
              <Link
                key={a.slug}
                to={`/news/${a.slug}`}
                onClick={() => trackClick("news-index-open", { slug: a.slug })}
                className="glass rounded-xl p-5 lift group flex flex-col"
                data-testid={`news-card-${a.slug}`}
              >
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-[var(--cobalt-bright)] mb-3">
                  <Newspaper className="w-3.5 h-3.5" />
                  <span className="truncate">{a.source || "News"}</span>
                  <span className="text-[var(--text-muted)]">·</span>
                  <Clock className="w-3 h-3 text-[var(--text-muted)]" />
                  <span className="text-[var(--text-muted)]">{timeAgo(a.first_seen)}</span>
                </div>
                <h2 className="font-display text-base font-semibold leading-snug flex-1">{a.title}</h2>
                <div className="mt-4 flex items-center justify-between text-xs text-[var(--text-muted)] font-mono">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" /> {a.likes || 0}</span>
                    <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {a.comments_count || 0}</span>
                  </div>
                  <span className="text-[var(--cobalt-bright)] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8" data-testid="news-pagination">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="btn-ghost inline-flex items-center gap-1 px-3 py-2 rounded-md text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <span className="text-xs font-mono text-[var(--text-muted)] px-3">Page {page} of {totalPages}</span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="btn-ghost inline-flex items-center gap-1 px-3 py-2 rounded-md text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
